// SPDX-License-Identifier: MIT
// Copyright contributors to the kepler.gl project
// Adapted from: https://github.com/keplergl/kepler.gl/blob/master/src/duckdb/src/table/duckdb-table-utils.ts
//
// This file contains utilities for DuckDB-WASM integration with Atlas Viewer,
// adapted from Kepler.gl's official implementation with modifications for
// our singleton pattern and security lineage tracking.

import type { AsyncDuckDBConnection } from '@duckdb/duckdb-wasm';

/**
 * Column description from DuckDB DESCRIBE query
 */
export interface DuckDBColumnDesc {
  column_name: string;
  column_type: string;
  null: string;
  key: string | null;
  default: string | null;
  extra: string | null;
}

/**
 * Map of column names to their DuckDB types
 */
export type DuckDBColumnTypesMap = Record<string, string>;

// SQL keywords for highlighting and autocomplete
export const SQL_KEYWORDS = [
  'SELECT',
  'FROM',
  'WHERE',
  'AND',
  'OR',
  'NOT',
  'IN',
  'LIKE',
  'BETWEEN',
  'ORDER',
  'BY',
  'ASC',
  'DESC',
  'LIMIT',
  'OFFSET',
  'JOIN',
  'LEFT',
  'RIGHT',
  'INNER',
  'OUTER',
  'FULL',
  'ON',
  'AS',
  'DISTINCT',
  'COUNT',
  'SUM',
  'AVG',
  'MIN',
  'MAX',
  'GROUP',
  'HAVING',
  'UNION',
  'ALL',
  'INSERT',
  'INTO',
  'VALUES',
  'UPDATE',
  'SET',
  'DELETE',
  'CREATE',
  'TABLE',
  'DROP',
  'ALTER',
  'INDEX',
  'PRIMARY',
  'KEY',
  'FOREIGN',
  'REFERENCES',
  'NULL',
  'TRUE',
  'FALSE',
  'CASE',
  'WHEN',
  'THEN',
  'ELSE',
  'END',
  'CAST',
  'IS',
  'COALESCE',
  'NULLIF',
];

// DuckDB spatial functions
export const SPATIAL_FUNCTIONS = [
  'ST_Distance',
  'ST_Contains',
  'ST_Intersects',
  'ST_Buffer',
  'ST_Area',
  'ST_Within',
  'ST_Crosses',
  'ST_Touches',
  'ST_Overlaps',
  'ST_Disjoint',
  'ST_Union',
  'ST_Intersection',
  'ST_Difference',
  'ST_SymDifference',
  'ST_Centroid',
  'ST_Boundary',
  'ST_ConvexHull',
  'ST_Envelope',
  'ST_AsText',
  'ST_AsWKB',
  'ST_AsGeoJSON',
  'ST_GeomFromText',
  'ST_GeomFromWKB',
  'ST_GeomFromGeoJSON',
  'ST_Point',
  'ST_MakeLine',
  'ST_MakePolygon',
  'ST_X',
  'ST_Y',
  'ST_Length',
  'ST_Perimeter',
  'ST_NumPoints',
  'ST_StartPoint',
  'ST_EndPoint',
  'ST_PointN',
  'ST_Transform',
];

/**
 * BigInt types that need to be cast to DOUBLE for JavaScript compatibility
 */
const BIGINT_TYPES = ['BIGINT', 'UBIGINT', 'HUGEINT', 'UHUGEINT'];

/**
 * Geometry type name in DuckDB
 */
const GEOMETRY_TYPE = 'GEOMETRY';

/**
 * Quote a table name for safe SQL usage
 * Handles qualified names (schema.table) and already-quoted identifiers
 */
export function quoteTableName(tableName: string): string {
  // Already quoted
  if (tableName.startsWith('"') && tableName.endsWith('"')) {
    return tableName;
  }

  // Handle qualified names (schema.table)
  if (tableName.includes('.')) {
    return tableName
      .split('.')
      .map(part => {
        if (part.startsWith('"') && part.endsWith('"')) return part;
        return `"${part.replace(/"/g, '""')}"`;
      })
      .join('.');
  }

  return `"${tableName.replace(/"/g, '""')}"`;
}

/**
 * Quote a column name for safe SQL usage
 */
export function quoteColumnName(columnName: string): string {
  if (columnName.startsWith('"') && columnName.endsWith('"')) {
    return columnName;
  }
  return `"${columnName.replace(/"/g, '""')}"`;
}

/**
 * Sanitize a filename to create a valid DuckDB table name
 * - Removes file extension
 * - Replaces invalid characters with underscores
 * - Prevents numeric prefix
 */
export function sanitizeDuckDBTableName(fileName: string): string {
  // Remove file extension
  let name = fileName.replace(/\.[^/.]+$/, '');

  // Replace invalid characters with underscores
  name = name.replace(/[^a-zA-Z0-9_]/g, '_');

  // Prevent leading numbers
  if (/^[0-9]/.test(name)) {
    name = 't_' + name;
  }

  // Truncate if too long (DuckDB max identifier length is 127)
  if (name.length > 120) {
    name = name.substring(0, 120);
  }

  return name;
}

/**
 * Get column types from a DuckDB table using DESCRIBE query
 */
export async function getDuckDBColumnTypes(
  conn: AsyncDuckDBConnection,
  tableName: string
): Promise<DuckDBColumnDesc[]> {
  const quotedTable = quoteTableName(tableName);
  const result = await conn.query(`DESCRIBE ${quotedTable}`);
  const rows = result.toArray();

  return rows.map((row: Record<string, unknown>) => ({
    column_name: String(row['column_name'] || row[0] || ''),
    column_type: String(row['column_type'] || row[1] || ''),
    null: String(row['null'] || row[2] || 'YES'),
    key: row['key'] !== undefined ? String(row['key']) : null,
    default: row['default'] !== undefined ? String(row['default']) : null,
    extra: row['extra'] !== undefined ? String(row['extra']) : null,
  }));
}

/**
 * Convert column descriptions to a map of name -> type
 */
export function getDuckDBColumnTypesMap(columns: DuckDBColumnDesc[]): DuckDBColumnTypesMap {
  const map: DuckDBColumnTypesMap = {};
  for (const col of columns) {
    map[col.column_name] = col.column_type;
  }
  return map;
}

/**
 * Generate SQL SELECT that casts GEOMETRY columns to WKB and BIGINT to DOUBLE
 * for Kepler.gl compatibility
 *
 * @param tableName - The source table name
 * @param columns - Column descriptions from getDuckDBColumnTypes
 * @returns SQL SELECT statement with necessary type conversions
 */
export function castDuckDBTypesForKepler(tableName: string, columns: DuckDBColumnDesc[]): string {
  const quotedTable = quoteTableName(tableName);

  const selectClauses = columns.map(col => {
    const quotedCol = quoteColumnName(col.column_name);
    const upperType = col.column_type.toUpperCase();

    // Cast GEOMETRY to WKB for Arrow transport
    if (upperType === GEOMETRY_TYPE || upperType.startsWith('GEOMETRY')) {
      return `ST_AsWKB(${quotedCol}) AS ${quotedCol}`;
    }

    // Cast BigInt types to DOUBLE to prevent JS BigInt serialization issues
    if (BIGINT_TYPES.includes(upperType)) {
      return `CAST(${quotedCol} AS DOUBLE) AS ${quotedCol}`;
    }

    return quotedCol;
  });

  return `SELECT ${selectClauses.join(', ')} FROM ${quotedTable}`;
}

/**
 * Remove SQL comments (both block and line comments)
 */
export function removeSQLComments(sql: string): string {
  // Remove block comments /* ... */
  let result = sql.replace(/\/\*[\s\S]*?\*\//g, '');

  // Remove line comments -- ...
  result = result.replace(/--[^\n]*(\n|$)/g, '$1');

  return result;
}

/**
 * Split SQL string into individual statements
 * Handles quoted strings and comments properly
 */
export function splitSqlStatements(sql: string): string[] {
  const cleaned = removeSQLComments(sql);
  const statements: string[] = [];
  let current = '';
  let inSingleQuote = false;
  let inDoubleQuote = false;

  for (let i = 0; i < cleaned.length; i++) {
    const char = cleaned[i];
    const prevChar = i > 0 ? cleaned[i - 1] : '';

    // Handle escape sequences
    if (prevChar === '\\') {
      current += char;
      continue;
    }

    // Track quote state
    if (char === "'" && !inDoubleQuote) {
      inSingleQuote = !inSingleQuote;
    } else if (char === '"' && !inSingleQuote) {
      inDoubleQuote = !inDoubleQuote;
    }

    // Split on semicolons outside quotes
    if (char === ';' && !inSingleQuote && !inDoubleQuote) {
      const trimmed = current.trim();
      if (trimmed.length > 0) {
        statements.push(trimmed);
      }
      current = '';
      continue;
    }

    current += char;
  }

  // Add final statement if present
  const trimmed = current.trim();
  if (trimmed.length > 0) {
    statements.push(trimmed);
  }

  return statements;
}

/**
 * Check if a SQL statement is a SELECT query using EXPLAIN
 */
export async function checkIsSelectQuery(
  conn: AsyncDuckDBConnection,
  sql: string
): Promise<boolean> {
  try {
    // Use EXPLAIN to check query type without executing
    await conn.query(`EXPLAIN ${sql}`);

    // If EXPLAIN succeeds, check if it starts with SELECT
    const upperSql = sql.trim().toUpperCase();
    return upperSql.startsWith('SELECT') || upperSql.startsWith('WITH');
  } catch {
    return false;
  }
}

/**
 * Safely drop a table if it exists
 */
export async function dropTableIfExists(
  conn: AsyncDuckDBConnection,
  tableName: string
): Promise<void> {
  const quotedTable = quoteTableName(tableName);
  try {
    await conn.query(`DROP TABLE IF EXISTS ${quotedTable}`);
  } catch (error) {
    console.warn(`[DuckDB] Failed to drop table ${tableName}:`, error);
  }
}

/**
 * Extract table names referenced in a SQL query
 * This is a simple heuristic - not a full SQL parser
 */
export function extractTableNamesFromSQL(sql: string): string[] {
  const cleaned = removeSQLComments(sql).toUpperCase();
  const tableNames = new Set<string>();

  // Match FROM and JOIN clauses
  const patterns = [/FROM\s+(["`]?[\w.]+["`]?)/gi, /JOIN\s+(["`]?[\w.]+["`]?)/gi];

  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(cleaned)) !== null) {
      const tableName = match[1].replace(/["`]/g, '');
      tableNames.add(tableName);
    }
  }

  return Array.from(tableNames);
}

/**
 * Check if a column type is a geometry type
 */
export function isGeometryType(type: string): boolean {
  const upper = type.toUpperCase();
  return upper === 'GEOMETRY' || upper.startsWith('GEOMETRY(');
}

/**
 * Check if a column type is a numeric type
 */
export function isNumericType(type: string): boolean {
  const upper = type.toUpperCase();
  const numericTypes = [
    'TINYINT',
    'SMALLINT',
    'INTEGER',
    'INT',
    'BIGINT',
    'UTINYINT',
    'USMALLINT',
    'UINTEGER',
    'UINT',
    'UBIGINT',
    'HUGEINT',
    'UHUGEINT',
    'FLOAT',
    'REAL',
    'DOUBLE',
    'DECIMAL',
    'NUMERIC',
  ];
  return numericTypes.some(t => upper.startsWith(t));
}

/**
 * Generate a unique table name by appending a suffix if needed
 */
export function generateUniqueTableName(baseName: string, existingNames: Set<string>): string {
  const name = sanitizeDuckDBTableName(baseName);

  if (!existingNames.has(name)) {
    return name;
  }

  let counter = 1;
  while (existingNames.has(`${name}_${counter}`)) {
    counter++;
  }

  return `${name}_${counter}`;
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Format duration in milliseconds to human-readable string
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

/**
 * Generate a unique ID for a new Kepler.gl dataset created from SQL query results
 *
 * NOTE: This differs from generateUniqueTableName() in purpose:
 * - generateUniqueTableName(): For DuckDB table names - must avoid conflicts with existing tables
 *   in DuckDB's catalog. Uses incremental suffixes (_1, _2) to prevent table recreation issues.
 * - generateDatasetId(): For Kepler.gl dataset IDs - uses timestamp + random suffix to ensure
 *   uniqueness across time. Each query result is a new dataset, no conflict checking needed.
 *
 * @returns A unique dataset ID in format: sql_result_<timestamp>_<random>
 * @example 'sql_result_1702647893245_k7x9m2a'
 */
export function generateDatasetId(): string {
  return `sql_result_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}
